import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, map, of, tap, throwError } from 'rxjs';
import { ProjectService } from '../../shared/services/project/project.service';
import { TimeSeriesTablesService } from '../../features/timeseries-data/services/timeseries-tables.service';
import { TimeSeriesTable } from '../../features/timeseries-data/models/timeseries-table.model';
import {
  AddTimeSeriesTableDialog,
  type AddTimeSeriesTableResult,
} from '../../features/timeseries-data/dialogs/add-timeseries-table-dialog/add-timeseries-table-dialog';
import {
  EditTimeSeriesTableDialog,
  type EditTimeSeriesTableResult,
} from '../../features/timeseries-data/dialogs/edit-timeseries-table-dialog/edit-timeseries-table-dialog';
import {
  SectionManagerDialog,
  type SectionManagerDialogAdapter,
  type SectionManagerDialogConfig,
} from '../../shared/common/section-manager-dialog/section-manager-dialog';
import {
  SharePermissionsDialog,
  type SharePermissionsDialogData,
} from '../../shared/common/share-permissions-dialog/share-permissions-dialog';
import { RoleService } from '../../shared/services/permissions-management/role.service';
import {
  ImpactConfirmationDialog,
  type ImpactConfirmDialogData,
} from '../../shared/common/impact-confirmation-dialog/impact-confirmation-dialog';
import { ConfirmationDialog } from '../../shared/common/confirmation-dialog/confirmation-dialog';

@Injectable({ providedIn: 'root' })
export class TimeSeriesTablesUiService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);
  private readonly tablesService = inject(TimeSeriesTablesService);
  private readonly roleService = inject(RoleService);

  private get isMultiPlantMode(): boolean {
    return this.projectService.currentProject()?.isMultiPlant ?? false;
  }

  openAddTableDialog(): void {
    const currentProject = this.projectService.currentProject();
    if (!currentProject || this.isMultiPlantMode) return;

    this.dialog
      .open(AddTimeSeriesTableDialog)
      .afterClosed()
      .subscribe(async (result?: AddTimeSeriesTableResult) => {
        if (!result) return;

        const created = await firstValueFrom(
          this.tablesService.create({
            name: result.name,
            description: result.description || undefined,
            plantId: currentProject.plantMeta?.id ?? '',
            projectId: currentProject.id,
            fields: [],
          }),
        );

        this.tablesService.addTable(created);
        await this.router.navigate(['/timeseries-data', created.id]);
      });
  }

  openSectionManagerDialog(): void {
    const plantId = this.projectService.singlePlantId ?? undefined;
    const adapter: SectionManagerDialogAdapter = {
      loadItems: () =>
        of(
          [...this.tablesService.tables()]
            .sort((a, b) => a.order - b.order)
            .map((t) => ({
              id: t.id,
              key: t.id,
              label: t.name,
              visible: t.visible,
              group: t.group,
            })),
        ),
      createItem: (dto) => {
        const currentProject = this.projectService.currentProject();
        if (!currentProject || this.isMultiPlantMode) {
          return throwError(
            () => new Error('Cannot create tables in multi-plant mode'),
          );
        }

        return this.tablesService
          .create({
            name: dto.label,
            plantId: currentProject?.plantMeta?.id ?? '',
            projectId: currentProject?.id ?? '',
            fields: [],
          })
          .pipe(
            tap((created) => this.tablesService.addTable(created)),
            map((created) => ({
              id: created.id,
              key: created.id,
              label: created.name,
              visible: created.visible ?? true,
            })),
          );
      },
      updateItem: (id, dto) => {
        if (!plantId) {
          return throwError(() => new Error('plantId is required'));
        }
        return this.tablesService
          .update(id, plantId, {
            name: dto.label,
            order: dto.order,
            visible: dto.visible,
            group: dto.group,
          })
          .pipe(tap((updated) => this.tablesService.updateTable(updated)));
      },
      deleteItem: (item) =>
        this.tablesService.remove(item.id, plantId).pipe(
          tap(() => {
            this.tablesService.removeTable(item.id);
            if (this.router.url.includes(item.id)) {
              void this.router.navigate(['/timeseries-data']);
            }
          }),
        ),
      confirmDelete: (item) => this.confirmTableDelete(item.id, plantId),
    };

    this.dialog.open(SectionManagerDialog, {
      width: '860px',
      data: {
        adapter,
        hideKeys: true,
        hideAdd: this.isMultiPlantMode,
        hideDelete: this.isMultiPlantMode,
        groupOptions: this.tablesService.uniqueGroups(),
      } satisfies SectionManagerDialogConfig,
    });
  }

  private async confirmTableDelete(tableId: string, plantId: string | undefined): Promise<boolean> {
    if (!plantId) {
      return firstValueFrom(
        this.dialog.open(ConfirmationDialog, { data: { confirmButtonType: 'delete' } }).afterClosed(),
      ).then((r) => r ?? false);
    }

    const impact = await firstValueFrom(this.tablesService.getShareImpact(tableId, plantId, [], true));
    const hasImpact = impact.dashboards.length > 0 || impact.reports.length > 0 || (impact.metrics?.length ?? 0) > 0;

    if (hasImpact) {
      const dialogData: ImpactConfirmDialogData = { impact, action: 'delete' };
      return firstValueFrom(
        this.dialog.open(ImpactConfirmationDialog, { data: dialogData }).afterClosed(),
      ).then((r) => r ?? false);
    }

    return firstValueFrom(
      this.dialog.open(ConfirmationDialog, { data: { confirmButtonType: 'delete' } }).afterClosed(),
    ).then((r) => r ?? false);
  }

  async openShareDialog(table: TimeSeriesTable): Promise<void> {
    const plantId = this.projectService.singlePlantId;
    if (!plantId) return;

    const allRoles = await firstValueFrom(this.roleService.getAllRoles());
    const currentPermissions = await firstValueFrom(
      this.tablesService.getShare(table.id, plantId),
    );

    const dialogData: SharePermissionsDialogData = {
      title: 'Share timeseries table',
      itemName: table.name,
      currentPermissions,
      allRoles,
      checkImpact: (keepRoleIds) =>
        firstValueFrom(this.tablesService.getShareImpact(table.id, plantId, keepRoleIds)),
    };

    const ref = this.dialog.open<SharePermissionsDialog, SharePermissionsDialogData, typeof currentPermissions | null>(
      SharePermissionsDialog,
      { data: dialogData, width: '560px' },
    );

    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;

    const keepRoleIds = [...result.admin, ...result.edit, ...result.view];
    const impact = await firstValueFrom(this.tablesService.getShareImpact(table.id, plantId, keepRoleIds));
    const hasImpact = impact.dashboards.length > 0 || impact.reports.length > 0 || (impact.metrics?.length ?? 0) > 0;

    if (hasImpact) {
      const confirmed = await firstValueFrom(
        this.dialog.open(ImpactConfirmationDialog, {
          data: { impact, action: 'remove_access' } satisfies ImpactConfirmDialogData,
        }).afterClosed(),
      );
      if (!confirmed) return;
    }

    await firstValueFrom(this.tablesService.updateShare(table.id, plantId, { sharePermissions: result }));
    await this.tablesService.loadTables(plantId);
  }

  openEditTableDialog(table: TimeSeriesTable): void {
    this.dialog
      .open(EditTimeSeriesTableDialog, {
        data: {
          id: table.id,
          name: table.name,
          description: table.description,
          timezone: table.timezone,
          displayTimezone: table.displayTimezone,
          timestampField: table.timestampField,
          duplicateHandling: table.duplicateHandling,
          period: table.period,
          allowEditSubmittedRows: table.allowEditSubmittedRows,
          blockFutureDates: table.blockFutureDates,
          group: table.group,
          availableGroups: this.tablesService.uniqueGroups(),
        },
      })
      .afterClosed()
      .subscribe(async (result?: EditTimeSeriesTableResult) => {
        if (!result) return;

        if (result.action === 'delete') {
          const plantId = this.projectService.singlePlantId;
          if (!plantId) return;
          await firstValueFrom(this.tablesService.remove(result.id, plantId));
          this.tablesService.removeTable(result.id);
          await this.router.navigate(['/timeseries-data']);
          return;
        }

        const updated = await firstValueFrom(
          this.tablesService.update(table.id, table.plantId, {
            name: result.name,
            description: result.description || undefined,
            group: result.group,
            displayTimezone: result.displayTimezone,
            ingestion: {
              timezone: result.timezone,
              timestampField: result.timestampField,
              duplicateHandling: result.duplicateHandling,
              period: result.period,
              allowEditSubmittedRows: result.allowEditSubmittedRows,
              blockFutureDates: result.blockFutureDates,
            },
          }),
        );

        this.tablesService.updateTable(updated);
      });
  }
}
